import React from 'react';
import { IFormulaItemData } from '../../../ModelBuilderContext';
import ColumnResize from '~/components/FormulasTable/ColumnResize';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import FormulaNode from './FormulaNode';
import { IRecipe, IVariableTypeEnum } from '~/services/parallel/formulas.types';
import useFormulaContext from '~/pages/FinancialModel/useFormulaContext';

interface IProps {
  rowIndex: number;
  columnWidth: number;
  data: IFormulaItemData;
  columnIndex: number;
}

const ModelBuilderExpression = ({ columnWidth, columnIndex, data }: IProps): React.ReactElement => {
  const { formulaDictionary } = useFormulaContext();

  const buildContentFromRecipe = (recipe: IRecipe): string => {
    return recipe.expression.replace(/\$\d+/g, (match) => {
      const variable = match.slice(1);
      let variableName = variable;

      const variableType = recipe.variables[`$${variable}`].type;
      switch (variableType) {
        case IVariableTypeEnum.Formula: {
          const formulaUuid = recipe.variables[`$${variable}`].formulaUuid;
          if (formulaUuid && formulaUuid in formulaDictionary) {
            const matchedFormula = formulaDictionary[formulaUuid];
            variableName = matchedFormula.recipe.name;
          }
          break;
        }
        case IVariableTypeEnum.Self:
          variableName = recipe.name;
          break;
        case IVariableTypeEnum.Constant:
          variableName = recipe.variables[`$${variable}`].constantValue?.toString() ?? '';
          return `<span className="mx-1">${variableName}</span>`; // Return constant value directly
        case IVariableTypeEnum.Calculated: {
          const calculationType = recipe.variables[`$${variable}`].calculationType;
          variableName = calculationType ?? '';
          break;
        }
        default:
          return `<span className="mx-1">${variableName}</span>`; // Return text directly
      }

      return `<span data-formula-node data-label="${variableName}"></span>`;
    });
  };

  const editor = useEditor({
    extensions: [StarterKit, FormulaNode],
    content: buildContentFromRecipe(data.formula.recipe),
    editorProps: {
      attributes: {
        // Classes are applied to the editor input itself
        class:
          'rounded w-full !whitespace-nowrap py-2 px-4 focus:outline-none focus:border border border-transparent focus:border-green h-[48px] flex items-center gap-1',
      },
    },
  });

  return (
    <div
      className="relative flex justify-between border-r border-neutral-50"
      style={{ width: `${columnWidth}px`, minWidth: `${columnWidth}px`, boxShadow: '6px 0px 8px rgba(0, 0, 0, 0.03)' }}
    >
      {/* Classes are applied to the editor container */}
      <EditorContent editor={editor} className="w-full overflow-hidden" />

      <ColumnResize columnIndex={columnIndex} />
    </div>
  );
};

export default ModelBuilderExpression;
